import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle, Loader2 } from "lucide-react";
import { getActiveVariant } from "../lib/variant";
import { trackEvent } from "../lib/analytics";

const API_URL = process.env.REACT_APP_API_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = { name: "", email: "", company: "", phone: "", message: "", website: "" };

const validate = (values) => {
  const errors = {};
  if (!values.name.trim()) errors.name = "Please enter your name.";
  if (!EMAIL_RE.test(values.email.trim())) errors.email = "Please enter a valid email.";
  if (!values.message.trim()) errors.message = "Please tell us a bit about your workflow.";
  return errors;
};

const fieldClass =
  "w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/30";

const Field = ({ id, label, error, children, optional = false }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-sm font-medium text-slate-200">
      {label}
      {optional && <span className="ml-2 text-xs font-normal text-slate-500">optional</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} className="mt-2 text-sm text-rose-300">
        {error}
      </p>
    )}
  </div>
);

const ContactForm = () => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [serverError, setServerError] = useState("");
  const renderedAt = useRef(0);

  useEffect(() => {
    renderedAt.current = Date.now();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validate(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setStatus("submitting");
    setServerError("");

    const variant = getActiveVariant();
    const sourcePage = window.location.pathname;

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ...values,
          renderedAt: renderedAt.current,
          sourcePage,
          variant,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.ok) {
        setStatus("success");
        trackEvent("generate_lead", { variant, source_page: sourcePage });
        return;
      }

      if (data.fields) setErrors(data.fields);
      setServerError(data.error || "Something went wrong. Please try again.");
      setStatus("error");
    } catch {
      setServerError("We could not reach the server. Please email contact@tier-1.io.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-cyan-300/30 bg-cyan-400/10 p-8 text-center">
        <CheckCircle className="mx-auto h-10 w-10 text-cyan-300" aria-hidden="true" />
        <h3 className="mt-4 text-xl font-bold text-white">Thanks — your message is on its way.</h3>
        <p className="mt-2 leading-7 text-slate-300">
          We read every message and will reply with the best next step, usually within one business day.
        </p>
      </div>
    );
  }

  const submitting = status === "submitting";

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="name" label="Name" error={errors.name}>
          <input
            id="name"
            name="name"
            type="text"
            value={values.name}
            onChange={handleChange}
            autoComplete="name"
            className={fieldClass}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? "name-error" : undefined}
          />
        </Field>
        <Field id="email" label="Email" error={errors.email}>
          <input
            id="email"
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            autoComplete="email"
            className={fieldClass}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? "email-error" : undefined}
          />
        </Field>
        <Field id="company" label="Company" optional>
          <input
            id="company"
            name="company"
            type="text"
            value={values.company}
            onChange={handleChange}
            autoComplete="organization"
            className={fieldClass}
          />
        </Field>
        <Field id="phone" label="Phone" optional>
          <input
            id="phone"
            name="phone"
            type="tel"
            value={values.phone}
            onChange={handleChange}
            autoComplete="tel"
            className={fieldClass}
          />
        </Field>
      </div>

      <Field id="message" label="What workflow should be easier?" error={errors.message}>
        <textarea
          id="message"
          name="message"
          rows={5}
          value={values.message}
          onChange={handleChange}
          placeholder="Tell us what is manual, repetitive, or slipping through the cracks."
          className={`${fieldClass} resize-y`}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? "message-error" : undefined}
        />
      </Field>

      {/* Honeypot: hidden from real users, attractive to bots. */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={handleChange}
        />
      </div>

      {serverError && (
        <p role="alert" className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {serverError}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex w-full items-center justify-center rounded-full bg-cyan-400 px-7 py-4 font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
            Sending…
          </>
        ) : (
          <>
            Send message
            <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
